import { useAction, useAtom } from '@reatom/npm-react'
import { motion } from 'motion/react'
import { useMemo, useRef } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import {
  FadeOverflow,
  hiddenScrollbarCss,
  HorizontalList,
} from '@root/shared/ui-kit'

import { reatomTopbarPositions } from '../atoms'
import { TopbarError } from '../common/topbar-error'
import { TopbarPairCount } from '../common/topbar-pair-count'
import { TopbarPairItemSkeleton } from '../common/topbar-pair-item-skeleton'
import { TOPBAR_POSITIONS_VARIANTS } from '../constants'

import { TopbarPosition } from './topbar-position'

export const TopbarPositions: React.FC = () => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomTopbarPositions(), [])
  const [positionsState] = useAtom(model.visiblePositionsStateAtom)
  const refreshPositions = useAction(model.positionsStateAtom.refresh)
  const handleReload = () => refreshPositions({ strategy: 'full' })
  const scrollRef = useRef<HTMLDivElement>(null)

  const renderPositions = () =>
    AsyncStateN.matchWaiting(positionsState, {
      Success: ({ data }) => {
        if (data.length === 0) {
          return <TopbarError onReload={handleReload} intent="empty" />
        }

        return (
          <HorizontalList transformScrollBehavior scrollRef={scrollRef}>
            <ListGroup ref={scrollRef}>
              {data.map((position) => (
                <Item key={position.pair.address} position={position} />
              ))}
            </ListGroup>
          </HorizontalList>
        )
      },
      Error: () => <TopbarError onReload={handleReload} />,
      Waiting: () => (
        <List>
          <TopbarPairItemSkeleton />
          <TopbarPairItemSkeleton />
          <TopbarPairItemSkeleton />
        </List>
      ),
    })

  return (
    <Root
      variants={TOPBAR_POSITIONS_VARIANTS}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <TopbarPairCount
        count={positionsState.value?.meta.total ?? 0}
        isLoading={!AsyncStateN.isReady(positionsState)}
      />
      <ListContainer
        inlineStart={12}
        inlineEnd={12}
        mode="always"
        variant="horizontal"
        key={positionsState.type}
      >
        {renderPositions()}
      </ListContainer>
    </Root>
  )
}

const Root = styled(motion.div)`
  padding-inline: 0 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  min-width: 0;
`

const ListContainer = styled(FadeOverflow)`
  min-width: 0;
  overflow-x: auto;

  ${hiddenScrollbarCss}
`

const List = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  margin-inline: 12px;
  width: min-content;

  margin-top: 1px;
`

const Item = styled(TopbarPosition)`
  padding: 0 5px;
`

const ListGroup = styled(FadeOverflow)`
  display: flex;
  width: 100%;
  padding-inline: 2px 10px;

  position: relative;
  overflow-y: hidden;

  ${hiddenScrollbarCss}
`
